import { writable } from 'svelte/store';

import type { Testimonial } from '$lib/types/Testimonial';
import type { Writable } from 'svelte/store';

export interface SliderController {
    rawOffset: number;
    offset: Writable<number>;
    interval: NodeJS.Timer | undefined;
    timeout: NodeJS.Timer | undefined;
    paused: Writable<boolean>;
    remainingDuration: number;
    startTime?: number;
    run: () => void;
    start: () => void;
    restart: () => void;
    next: () => void;
    back: () => void;
    pause: () => void;
    resume: () => void;
}

const createSliderController = (
    items: Testimonial[] | string[],
    duration = 5000,
): SliderController => {
    let paused: boolean;

    const controller: SliderController = {
        rawOffset: 0,
        offset: writable(0),
        paused: writable(false),
        interval: undefined,
        timeout: undefined,
        // animation props
        startTime: 0,
        remainingDuration: 0,
        // actions
        run: () => {
            return;
        },
        start: () => {
            return;
        },
        restart: () => {
            return;
        },
        next: () => {
            return;
        },
        back: () => {
            return;
        },
        pause: () => {
            return;
        },
        resume: () => {
            return;
        },
    };

    controller.paused.subscribe((newPaused) => {
        paused = newPaused;
    });

    controller.run = () => {
        // Reset start time & ramaining duration
        controller.remainingDuration = 0;
        controller.startTime = new Date().getTime();

        if (controller.rawOffset < items.length - 1) {
            controller.rawOffset++;
            controller.offset.set(controller.rawOffset);
        } else {
            controller.rawOffset = 0;
            controller.offset.set(controller.rawOffset);
        }
    };

    controller.start = () => {
        // Reset start time & ramaining duration
        controller.remainingDuration = 0;
        controller.startTime = new Date().getTime();

        controller.interval = setInterval(() => {
            if (!paused) {
                controller.run();
            }
        }, duration);
    };

    controller.restart = () => {
        controller.paused.set(false);
        clearInterval(controller.interval);
        controller.start();
    };

    controller.next = () => {
        if (controller.rawOffset + 1 < items.length) {
            controller.rawOffset++;
            controller.offset.set(controller.rawOffset);
            controller.restart();
        } else {
            controller.rawOffset = 0;
            controller.offset.set(controller.rawOffset);
            controller.restart();
        }
    };

    controller.back = () => {
        if (controller.rawOffset - 1 >= 0) {
            controller.rawOffset--;
            controller.offset.set(controller.rawOffset);
            controller.restart();
        } else {
            controller.rawOffset = items.length - 1;
            controller.offset.set(controller.rawOffset);
            controller.restart();
        }
    };

    controller.pause = () => {
        if (!paused) {
            controller.paused.set(true);

            // Clear interval & timeout
            clearInterval(controller.interval);
            clearTimeout(controller.timeout);

            const timePassed = controller.startTime
                ? new Date().getTime() - controller.startTime
                : 0;

            controller.remainingDuration =
                controller.remainingDuration > 0
                    ? controller.remainingDuration - timePassed
                    : duration - timePassed;
        }
    };

    controller.resume = () => {
        if (paused) {
            if (controller.remainingDuration > 0) {
                controller.paused.set(false);

                // Reset start time
                controller.startTime = new Date().getTime();

                // Go to next slide after remaining duration
                controller.timeout = setTimeout(() => {
                    controller.next();
                }, controller.remainingDuration);
            } else {
                controller.restart();
            }
        }
    };

    return controller;
};

export default createSliderController;
